import React, { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { StorageContext } from '../contexts/StorageContext';
import { getLanguageFromFile } from '../utils/fileUtils';
import LoadingSpinner from './LoadingSpinner';
import './FileDiff.css';

/**
 * Component for displaying differences between file versions
 */
function FileDiff() {
  const { fileId } = useParams();
  const { storage } = useContext(StorageContext);
  const [file, setFile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [oldVersion, setOldVersion] = useState(null);
  const [newVersion, setNewVersion] = useState(null);
  const [oldContent, setOldContent] = useState('');
  const [newContent, setNewContent] = useState('');
  const [diffResult, setDiffResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load file and versions
  useEffect(() => {
    const loadFile = async () => {
      if (!fileId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get file metadata
        const fileData = await storage.getFile(fileId);
        
        if (!fileData) {
          setError('File not found');
          return;
        }
        
        setFile(fileData);
        
        // Get file versions
        const fileVersions = await storage.getAllFileVersions(fileId);
        const sortedVersions = fileVersions.sort((a, b) => b.version - a.version);
        
        setVersions(sortedVersions);
        
        if (sortedVersions.length >= 2) {
          // Default to comparing latest two versions
          setNewVersion(sortedVersions[0]);
          setOldVersion(sortedVersions[1]);
          setNewContent(sortedVersions[0].content);
          setOldContent(sortedVersions[1].content);
        } else if (sortedVersions.length === 1) {
          // Only one version available
          setNewVersion(sortedVersions[0]);
          setNewContent(sortedVersions[0].content);
          setOldContent('');
        }
      } catch (err) {
        console.error('Error loading file versions:', err);
        setError('Failed to load file versions');
      } finally {
        setLoading(false);
      }
    };
    
    loadFile();
  }, [fileId, storage]);
  
  // Compute diff when content changes
  useEffect(() => {
    const computeDiff = async () => {
      if (!oldContent && !newContent) return;
      
      try {
        // Load diff2html if not already loaded
        if (!window.Diff) {
          await loadDiffLibrary();
        }
        
        // Create diff
        const diff = window.Diff.createTwoFilesPatch(
          `${file?.path || 'previous'}`,
          `${file?.path || 'current'}`,
          oldContent || '',
          newContent || '',
          oldVersion ? `Version ${oldVersion.version}` : 'Empty file',
          newVersion ? `Version ${newVersion.version}` : 'Current version'
        );
        
        // Convert to HTML
        let diffHtml = '';
        
        if (window.Diff2Html) {
          const diffJson = window.Diff2Html.parse(diff);
          diffHtml = window.Diff2Html.html(diffJson, {
            drawFileList: false,
            matching: 'lines',
            outputFormat: 'side-by-side'
          });
        } else {
          // Fallback to plain text diff
          diffHtml = `<pre class="diff-fallback">${diff}</pre>`;
        }
        
        setDiffResult(diffHtml);
      } catch (err) {
        console.error('Error creating diff:', err);
        setError('Failed to generate diff view');
      }
    };
    
    computeDiff();
  }, [oldContent, newContent, oldVersion, newVersion, file]);
  
  // Load diff library dynamically
  const loadDiffLibrary = async () => {
    return new Promise((resolve) => {
      // Load diff library
      const diffScript = document.createElement('script');
      diffScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jsdiff/4.0.2/diff.min.js';
      diffScript.async = true;
      
      // Load diff2html after diff
      diffScript.onload = () => {
        const diff2htmlScript = document.createElement('script');
        diff2htmlScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/diff2html/3.4.17/diff2html.min.js';
        diff2htmlScript.async = true;
        
        // Load CSS
        const diff2htmlStyle = document.createElement('link');
        diff2htmlStyle.href = 'https://cdnjs.cloudflare.com/ajax/libs/diff2html/3.4.17/diff2html.min.css';
        diff2htmlStyle.rel = 'stylesheet';
        
        diff2htmlScript.onload = resolve;
        
        document.head.appendChild(diff2htmlStyle);
        document.head.appendChild(diff2htmlScript);
      };
      
      document.head.appendChild(diffScript);
    });
  };
  
  // Handle version change
  const handleOldVersionChange = async (e) => {
    const versionId = e.target.value;
    
    if (versionId === 'none') {
      setOldVersion(null);
      setOldContent('');
      return;
    }
    
    const selectedVersion = versions.find(v => v.id === versionId);
    if (selectedVersion) {
      setOldVersion(selectedVersion);
      setOldContent(selectedVersion.content);
    }
  };
  
  const handleNewVersionChange = async (e) => {
    const versionId = e.target.value;
    const selectedVersion = versions.find(v => v.id === versionId);
    
    if (selectedVersion) {
      setNewVersion(selectedVersion);
      setNewContent(selectedVersion.content);
    }
  };
  
  if (loading) {
    return (
      <div className="file-diff-loading">
        <LoadingSpinner message="Loading file versions..." />
      </div>
    );
  }
  
  if (error) {
    return <div className="file-diff-error">{error}</div>;
  }
  
  if (!file) {
    return <div className="file-diff-error">File not found</div>;
  }
  
  return (
    <div className="file-diff">
      <div className="file-diff-header">
        <h2 className="file-diff-title">{file.path}</h2>
        
        <div className="version-selectors">
          <div className="version-selector">
            <label htmlFor="old-version">Old Version:</label>
            <select 
              id="old-version"
              value={oldVersion?.id || 'none'}
              onChange={handleOldVersionChange}
            >
              <option value="none">Empty file</option>
              {versions.map(version => (
                <option key={version.id} value={version.id}>
                  Version {version.version} ({new Date(version.timestamp).toLocaleString()})
                </option>
              ))}
            </select>
          </div>
          
          <div className="version-selector">
            <label htmlFor="new-version">New Version:</label>
            <select 
              id="new-version"
              value={newVersion?.id || ''}
              onChange={handleNewVersionChange}
            >
              {versions.map(version => (
                <option key={version.id} value={version.id}>
                  Version {version.version} ({new Date(version.timestamp).toLocaleString()})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      <div className="file-diff-content">
        {diffResult ? (
          <div 
            className="diff-result" 
            dangerouslySetInnerHTML={{ __html: diffResult }}
          />
        ) : (
          <div className="no-diff-message">
            {versions.length <= 1 ? (
              'This file has only one version. No diff available.'
            ) : (
              'Select versions to compare'
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default FileDiff;
